import { emptySplitApi } from "./empty-split-api";
import {
  NoahDevice,
  NoahEventSubscriptionsResponse,
  NoahIntegrationRequest,
  NoahPatientResponse,
  NoahSession,
  SyncNoahPatientsRequest
} from "./types/noah.type";
import { Patient } from "./types/patients.type";

export const extendedNoahApi = emptySplitApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getNoahPatient: builder.query<NoahPatientResponse, number>({
      query: (patientId: number) => `/noah/${patientId}/noah-es`,
      providesTags: ["NoahPatient"]
    }),
    editNoahLegacyId: builder.mutation<
      void,
      { patient: number; legacy_patient_id: number }
    >({
      query: ({ patient, legacy_patient_id }) => ({
        url: `/noah/${patient}/update-legacy-id/`,
        method: "PATCH",
        body: { legacy_patient_id }
      }),
      invalidatesTags: [
        "NoahPatient",
        "NoahSessions",
        "NoahDevices",
        "Patients",
        "Patient"
      ]
    }),
    syncNoahSessions: builder.mutation<
      void,
      { patient_id: number; current_clinic: number }
    >({
      query: ({ patient_id, current_clinic }) => ({
        url: `/noah-sessions/sync/`,
        params: { patient_id, current_clinic },
        method: "POST"
      }),
      invalidatesTags: ["NoahSessions", "NoahDevices", "Patients", "Patient"]
    }),
    getNoahEventSubscriptions: builder.query<
      NoahEventSubscriptionsResponse,
      { tenant_id: string }
    >({
      query: ({ tenant_id }) => ({
        url: `/noah-event-subscriptions/`,
        params: { tenant_id },
        method: "GET"
      }),
      providesTags: ["NoahEventSubscriptions"]
    }),
    syncNoahEventSubscriptions: builder.mutation<{ message: string }, string>({
      query: (tenant_id) => ({
        url: `/noah-event-subscriptions/`,
        body: { tenant_id },
        method: "POST"
      }),
      invalidatesTags: ["NoahEventSubscriptions"]
    }),
    deleteNoahEventSubscription: builder.mutation<
      { message: string },
      { event_subscription_id: string; tenant_id: string }
    >({
      query: ({ event_subscription_id, tenant_id }) => ({
        url: `/noah-event-subscriptions/${event_subscription_id}/`,
        body: { tenant_id },
        method: "DELETE"
      }),
      invalidatesTags: ["NoahEventSubscriptions"]
    }),
    syncNoahPatients: builder.mutation<
      { message: string },
      SyncNoahPatientsRequest
    >({
      query: (request) => ({
        url: `/noah-patient-matching/`,
        method: "POST",
        body: request
      })
    }),
    getNoahSessions: builder.query<NoahSession[], number>({
      query: (patient_id) => ({
        url: `/noah-sessions/`,
        params: { patient_id },
        method: "GET"
      }),
      providesTags: (result, _, patient_id) => [
        "NoahSessions",
        { id: patient_id, type: "NoahSessions" }
      ]
    }),
    createNoahPatient: builder.mutation<Patient, number>({
      query: (patientId) => ({
        url: `/noah/${patientId}/noah-es`,
        method: "POST"
      }),
      invalidatesTags: ["NoahPatient", "Patients", "Patient", "NoahSessions"]
    }),
    openPatientInNoah: builder.mutation<NoahIntegrationRequest, string>({
      query: (patientNoahId) => ({
        url: `https://localhost:8090/NoahWebIntegrationGUIStart/noah4/patientguid/${patientNoahId}`,
        method: "GET",
        headers: {
          Accept: "text/plain"
        },
        mode: "no-cors"
      })
    }),
    getNoahDevices: builder.query<NoahDevice[], number>({
      query: (patient_id) => ({
        url: `/noah-devices/`,
        params: { patient: patient_id },
        method: "GET"
      }),
      providesTags: ["NoahDevices"]
    })
  })
});

export const {
  useGetNoahPatientQuery,
  useEditNoahLegacyIdMutation,
  useCreateNoahPatientMutation,
  useGetNoahSessionsQuery,
  useSyncNoahSessionsMutation,
  useOpenPatientInNoahMutation,
  useSyncNoahEventSubscriptionsMutation,
  useSyncNoahPatientsMutation,
  useDeleteNoahEventSubscriptionMutation,
  useGetNoahEventSubscriptionsQuery,
  useGetNoahDevicesQuery
} = extendedNoahApi;
